import '../i18n/autoTranslate';
import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertCircle,
  ArrowUpRight,
  BarChart3,
  Bell,
  Check,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Download,
  Flag,
  Layers,
  LayoutDashboard,
  Loader2,
  Mail,
  MessageSquare,
  RefreshCw,
  Search,
  Settings,
  ShieldAlert,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AnimatePresence, motion } from "framer-motion";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:5000";

function getAuthToken() {
  // supports multiple naming conventions
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    ""
  );
}

async function apiRequest(path, { method = "GET", body, signal } = {}) {
  const token = getAuthToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  const isJson = (res.headers.get("content-type") || "").includes("application/json");
  const payload = isJson ? await res.json().catch(() => null) : await res.text().catch(() => "");

  if (!res.ok) {
    const message =
      (payload && typeof payload === "object" && (payload.error || payload.message)) ||
      (typeof payload === "string" && payload) ||
      `Request failed (${res.status})`;
    const err = new Error(message);
    // @ts-ignore
    err.status = res.status;
    // @ts-ignore
    err.payload = payload;
    throw err;
  }

  return payload;
}

function formatMoney(v) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "$0";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: n >= 100 ? 0 : 2,
  }).format(n);
}

function formatCompact(v) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat(undefined, { notation: "compact" }).format(n);
}

function formatPercent(v) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0%";
  return `${n.toFixed(2)}%`;
}

function clsx(...xs) {
  return xs.filter(Boolean).join(" ");
}

const pageAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const chip = {
  base: "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold",
  green: "border-emerald-500/20 bg-emerald-500/10 text-emerald-200",
  yellow: "border-amber-500/20 bg-amber-500/10 text-amber-200",
  red: "border-rose-500/20 bg-rose-500/10 text-rose-200",
  blue: "border-sky-500/20 bg-sky-500/10 text-sky-200",
  gray: "border-white/10 bg-white/5 text-white/70",
};

function GlassCard({ className = "", children }) {
  return (
    <div
      className={clsx(
        "relative rounded-2xl p-[1px] bg-gradient-to-br from-white/12 via-white/6 to-transparent",
        className
      )}
    >
      <div className="rounded-2xl border border-white/6 bg-[#0B0B12]/70 backdrop-blur-xl shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)]">
        {children}
      </div>
    </div>
  );
}

function CardHeader({ title, subtitle, right }) {
  return (
    <div className="flex items-start justify-between gap-4 p-5 pb-0">
      <div>
        <div className="text-sm font-semibold text-white/90">{title}</div>
        {subtitle ? <div className="mt-1 text-xs text-white/55">{subtitle}</div> : null}
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
  );
}

function CardBody({ className = "", children }) {
  return <div className={clsx("p-5", className)}>{children}</div>;
}

function Stat({ label, value, hint, icon: Icon, tone = "blue" }) {
  const tones = {
    blue: "from-sky-500/20 to-indigo-500/10 border-sky-500/20",
    green: "from-emerald-500/20 to-emerald-500/10 border-emerald-500/20",
    yellow: "from-amber-500/20 to-amber-500/10 border-amber-500/20",
    red: "from-rose-500/20 to-rose-500/10 border-rose-500/20",
    purple: "from-violet-500/20 to-fuchsia-500/10 border-violet-500/20",
    gray: "from-white/8 to-transparent border-white/10",
  };

  return (
    <motion.div whileHover={{ y: -2 }} className="h-full">
      <div
        className={clsx(
          "h-full rounded-2xl border bg-gradient-to-br p-4",
          tones[tone] || tones.blue
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-medium text-white/55">{label}</div>
            <div className="mt-1 text-2xl font-semibold tracking-tight text-white">{value}</div>
            {hint ? <div className="mt-1 text-xs text-white/45">{hint}</div> : null}
          </div>
          {Icon ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-2">
              <Icon className="h-5 w-5 text-white/80" />
            </div>
          ) : null}
        </div>
      </div>
    </motion.div>
  );
}

function Badge({ tone = "gray", children }) {
  return <span className={clsx(chip.base, chip[tone] || chip.gray)}>{children}</span>;
}

function Button({
  children,
  variant = "primary",
  size = "md",
  className = "",
  ...props
}) {
  const sizes = {
    sm: "h-9 px-3 text-xs",
    md: "h-10 px-4 text-sm",
  };
  const variants = {
    primary:
      "bg-white text-black hover:bg-white/90 shadow-[0_10px_25px_-12px_rgba(255,255,255,0.3)]",
    subtle: "bg-white/7 text-white hover:bg-white/10 border border-white/10",
    ghost: "bg-transparent text-white/70 hover:text-white hover:bg-white/5",
    danger: "bg-rose-500/15 text-rose-100 hover:bg-rose-500/20 border border-rose-500/20",
  };

  return (
    <button
      {...props}
      className={clsx(
        "inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition",
        sizes[size] || sizes.md,
        variants[variant] || variants.primary,
        className
      )}
    >
      {children}
    </button>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={clsx(
        "h-10 w-full rounded-xl border border-white/10 bg-white/5 px-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/15",
        className
      )}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={clsx(
        "h-10 w-full rounded-xl border border-white/10 bg-white/5 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-white/15",
        className
      )}
    >
      {children}
    </select>
  );
}

function Modal({ open, title, children, onClose, footer }) {
  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <button
            aria-label="Close modal"
            onClick={onClose}
            className="absolute inset-0 bg-black/70"
          />
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 420, damping: 32 }}
            className="relative w-full max-w-xl"
          >
            <GlassCard>
              <div className="flex items-center justify-between gap-3 p-5">
                <div className="text-base font-semibold text-white">{title}</div>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="px-5 pb-5">{children}</div>
              {footer ? <div className="border-t border-white/8 p-4">{footer}</div> : null}
            </GlassCard>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/5 p-10 text-center">
      <div className="mb-4 rounded-2xl border border-white/10 bg-white/5 p-3">
        <Icon className="h-6 w-6 text-white/80" />
      </div>
      <div className="text-sm font-semibold text-white/90">{title}</div>
      <div className="mt-1 max-w-sm text-xs text-white/55">{subtitle}</div>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "subscriptions", label: "Subscriptions", icon: CreditCard },
  { id: "plans", label: "Plans", icon: Layers },
  { id: "payments", label: "Payments", icon: BarChart3 },
  { id: "support", label: "Support", icon: MessageSquare },
  { id: "settings", label: "Settings", icon: Settings },
];

export default function AdminDashboard() {
  const [active, setActive] = useState("overview");

  // loading / error
  const [bootLoading, setBootLoading] = useState(true);
  const [bootError, setBootError] = useState(null);
  const [busy, setBusy] = useState(false);

  // overview data
  const [subDashboard, setSubDashboard] = useState(null);
  const [subStats, setSubStats] = useState(null);
  const [contactStats, setContactStats] = useState(null);
  const [issues, setIssues] = useState({ items: [], total: 0, limit: 20, offset: 0 });
  const [inquiries, setInquiries] = useState({ items: [], total: 0, page: 1, limit: 10, total_pages: 1 });
  const [recentPayments, setRecentPayments] = useState([]);

  // plans
  const [plans, setPlans] = useState([]);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planForm, setPlanForm] = useState({
    plan_type: "",
    name: "",
    monthly_price: "",
    annual_price: "",
    interviews_per_month: "",
    description: "",
    is_active: true,
  });

  // subscriptions table
  const [subsRows, setSubsRows] = useState([]);
  const [subsPagination, setSubsPagination] = useState({ page: 1, per_page: 20, total: 0, pages: 1 });
  const [subsFilters, setSubsFilters] = useState({ status: "", plan_type: "", q: "" });

  // support filters
  const [supportQ, setSupportQ] = useState("");
  const [issueStatus, setIssueStatus] = useState("");
  const [inquiryStatus, setInquiryStatus] = useState("");

  const overviewKpis = useMemo(() => {
    const dash = subDashboard?.dashboard;
    const stats = subStats?.statistics;

    const activeSubs = dash?.subscribers?.active ?? 0;
    const trialSubs = dash?.subscribers?.trialing ?? 0;
    const canceledSubs = dash?.subscribers?.canceled ?? 0;

    const revenueMonth = dash?.revenue_this_month ?? 0;
    const mrr = stats?.mrr ?? 0;
    const arr = stats?.arr ?? 0;

    const conv = stats?.trial_conversion_rate ?? 0;
    const churn = stats?.churn_rate ?? 0;

    const openInquiries =
      (contactStats?.statistics?.by_status?.new ?? 0) +
      (contactStats?.statistics?.by_status?.in_progress ?? 0);

    // issues endpoint doesn't expose a "new count" reliably (depends on model); we show total loaded + total.
    const issueTotal = issues?.total ?? 0;

    return {
      activeSubs,
      trialSubs,
      canceledSubs,
      revenueMonth,
      mrr,
      arr,
      conv,
      churn,
      openInquiries,
      issueTotal,
    };
  }, [subDashboard, subStats, contactStats, issues]);

  const statusDistribution = useMemo(() => {
    const d = subStats?.statistics?.status_distribution || {};
    return Object.entries(d).map(([name, value]) => ({ name, value: Number(value || 0) }));
  }, [subStats]);

  const revenueByPlan = useMemo(() => {
    const d = subStats?.statistics?.revenue_by_plan || {};
    return Object.entries(d).map(([plan_type, obj]) => ({
      plan_type,
      total_revenue: Number(obj?.total_revenue || 0),
      subscriber_count: Number(obj?.subscriber_count || 0),
    }));
  }, [subStats]);

  async function bootstrap() {
    setBootError(null);
    setBootLoading(true);

    try {
      const [dashboardRes, statsRes, plansRes, contactStatsRes, issuesRes, inquiriesRes, paymentsRes] =
        await Promise.all([
          apiRequest("/api/admin/subscriptions/dashboard"),
          apiRequest("/api/admin/subscriptions/statistics"),
          apiRequest("/api/subscriptions/plans"),
          apiRequest("/api/contact/statistics"),
          apiRequest("/api/issue-reports?limit=20&offset=0"),
          apiRequest("/api/contact/inquiries?limit=10&page=1"),
          apiRequest("/api/admin/subscriptions/payments/recent?limit=10"),
        ]);

      setSubDashboard(dashboardRes);
      setSubStats(statsRes);
      setPlans(plansRes?.plans || []);
      setContactStats(contactStatsRes);
      setIssues({
        items: issuesRes?.reports || [],
        total: issuesRes?.total || 0,
        limit: issuesRes?.limit || 20,
        offset: issuesRes?.offset || 0,
      });
      setInquiries({
        items: inquiriesRes?.inquiries || [],
        total: inquiriesRes?.total || 0,
        page: inquiriesRes?.page || 1,
        limit: inquiriesRes?.limit || 10,
        total_pages: inquiriesRes?.total_pages || 1,
      });
      setRecentPayments(paymentsRes?.payments || []);
    } catch (e) {
      setBootError(e);
    } finally {
      setBootLoading(false);
    }
  }

  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadSubscriptions({ page = 1 } = {}) {
    setBusy(true);
    try {
      const qs = new URLSearchParams();
      qs.set("page", String(page));
      qs.set("per_page", String(subsPagination.per_page || 20));
      if (subsFilters.status) qs.set("status", subsFilters.status);
      if (subsFilters.plan_type) qs.set("plan_type", subsFilters.plan_type);
      // backend doesn't support q in /all currently; keep client-side filter too

      const res = await apiRequest(`/api/admin/subscriptions/all?${qs.toString()}`);

      const items = res?.subscriptions || [];
      const filtered = subsFilters.q
        ? items.filter((x) => JSON.stringify(x).toLowerCase().includes(subsFilters.q.toLowerCase()))
        : items;

      setSubsRows(filtered);
      setSubsPagination(res?.pagination || { page, per_page: 20, total: 0, pages: 1 });
    } catch (e) {
      setBootError(e);
    } finally {
      setBusy(false);
    }
  }

  async function loadPayments(limit = 25) {
    setBusy(true);
    try {
      const res = await apiRequest(`/api/admin/subscriptions/payments/recent?limit=${limit}`);
      setRecentPayments(res?.payments || []);
    } catch (e) {
      setBootError(e);
    } finally {
      setBusy(false);
    }
  }

  async function loadSupport({ issuesOffset = 0, inquiriesPage = 1 } = {}) {
    setBusy(true);
    try {
      const issuesParams = new URLSearchParams();
      issuesParams.set("limit", "50");
      issuesParams.set("offset", String(issuesOffset));
      if (supportQ) issuesParams.set("q", supportQ);
      if (issueStatus) issuesParams.set("status", issueStatus);

      const inquiriesParams = new URLSearchParams();
      inquiriesParams.set("limit", "20");
      inquiriesParams.set("page", String(inquiriesPage));
      if (inquiryStatus) inquiriesParams.set("status", inquiryStatus);

      const [issuesRes, inquiriesRes, contactStatsRes] = await Promise.all([
        apiRequest(`/api/issue-reports?${issuesParams.toString()}`),
        apiRequest(`/api/contact/inquiries?${inquiriesParams.toString()}`),
        apiRequest("/api/contact/statistics"),
      ]);

      setIssues({
        items: issuesRes?.reports || [],
        total: issuesRes?.total || 0,
        limit: issuesRes?.limit || 50,
        offset: issuesRes?.offset || 0,
      });

      setInquiries({
        items: inquiriesRes?.inquiries || [],
        total: inquiriesRes?.total || 0,
        page: inquiriesRes?.page || 1,
        limit: inquiriesRes?.limit || 20,
        total_pages: inquiriesRes?.total_pages || 1,
      });

      setContactStats(contactStatsRes);
    } catch (e) {
      setBootError(e);
    } finally {
      setBusy(false);
    }
  }

  async function refreshAll() {
    setBusy(true);
    try {
      await bootstrap();
      if (active === "subscriptions") await loadSubscriptions({ page: subsPagination.page || 1 });
      if (active === "payments") await loadPayments(25);
      if (active === "support") await loadSupport({ issuesOffset: issues.offset || 0, inquiriesPage: inquiries.page || 1 });
    } finally {
      setBusy(false);
    }
  }

  function onNavigate(tabId) {
    setActive(tabId);

    // Lazy-load heavy tabs
    if (tabId === "subscriptions" && subsRows.length === 0) {
      loadSubscriptions({ page: 1 });
    }
    if (tabId === "payments") {
      loadPayments(25);
    }
    if (tabId === "support") {
      loadSupport({ issuesOffset: 0, inquiriesPage: 1 });
    }
  }

  async function createPlan() {
    setBusy(true);
    try {
      // Basic frontend validation (backend also validates)
      const required = ["plan_type", "name", "monthly_price", "annual_price", "interviews_per_month"];
      for (const k of required) {
        if (String(planForm[k] ?? "").trim() === "") {
          throw new Error(`Missing required field: ${k}`);
        }
      }

      const payload = {
        plan_type: planForm.plan_type.trim(),
        name: planForm.name.trim(),
        monthly_price: Number(planForm.monthly_price),
        annual_price: Number(planForm.annual_price),
        interviews_per_month: Number(planForm.interviews_per_month),
        description: planForm.description || null,
        is_active: Boolean(planForm.is_active),
      };

      if (![payload.monthly_price, payload.annual_price, payload.interviews_per_month].every((n) => Number.isFinite(n))) {
        throw new Error("Prices and interviews_per_month must be valid numbers");
      }

      await apiRequest("/api/admin/subscriptions/plans", { method: "POST", body: payload });

      // refresh plans list
      const plansRes = await apiRequest("/api/subscriptions/plans");
      setPlans(plansRes?.plans || []);
      setPlanModalOpen(false);
      setPlanForm({
        plan_type: "",
        name: "",
        monthly_price: "",
        annual_price: "",
        interviews_per_month: "",
        description: "",
        is_active: true,
      });
    } catch (e) {
      setBootError(e);
    } finally {
      setBusy(false);
    }
  }

  async function updatePlan(planId, patch) {
    setBusy(true);
    try {
      await apiRequest(`/api/admin/subscriptions/plans/${planId}`, { method: "PUT", body: patch });
      const plansRes = await apiRequest("/api/subscriptions/plans");
      setPlans(plansRes?.plans || []);
    } catch (e) {
      setBootError(e);
    } finally {
      setBusy(false);
    }
  }

  async function deletePlan(planId) {
    setBusy(true);
    try {
      await apiRequest(`/api/admin/subscriptions/plans/${planId}`, { method: "DELETE" });
      const plansRes = await apiRequest("/api/subscriptions/plans");
      setPlans(plansRes?.plans || []);
    } catch (e) {
      setBootError(e);
    } finally {
      setBusy(false);
    }
  }

  async function updateIssueReportStatus(reportId, status) {
    setBusy(true);
    try {
      await apiRequest(`/api/issue-reports/${reportId}`, { method: "PATCH", body: { status } });
      await loadSupport({ issuesOffset: issues.offset || 0, inquiriesPage: inquiries.page || 1 });
    } catch (e) {
      setBootError(e);
    } finally {
      setBusy(false);
    }
  }

  async function resolveInquiry(inquiryId) {
    setBusy(true);
    try {
      await apiRequest(`/api/contact/inquiry/${inquiryId}/resolve`, { method: "PUT", body: {} });
      await loadSupport({ issuesOffset: issues.offset || 0, inquiriesPage: inquiries.page || 1 });
    } catch (e) {
      setBootError(e);
    } finally {
      setBusy(false);
    }
  }

  const navBadges = useMemo(() => {
    const openInquiries =
      (contactStats?.statistics?.by_status?.new ?? 0) +
      (contactStats?.statistics?.by_status?.in_progress ?? 0);
    return {
      support: openInquiries > 0 ? String(openInquiries) : null,
    };
  }, [contactStats]);

  // ----------------------------- UI blocks -----------------------------

  const Header = (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-1.5">
            <Sparkles className="h-4 w-4 text-white/70" />
            <span className="text-xs font-semibold text-white/80">InterviewGenieAI</span>
          </div>
          <Badge tone="blue">
            <ShieldAlert className="h-3.5 w-3.5" /> Admin
          </Badge>
        </div>
        <div className="mt-3 text-2xl font-semibold tracking-tight text-white">Control Center</div>
        <div className="mt-1 text-sm text-white/55">Subscriptions, revenue, and support health — all in one place.</div>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="subtle" onClick={refreshAll} disabled={busy || bootLoading}>
          {busy || bootLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </Button>
        <Button variant="primary" onClick={() => onNavigate("support")}
          className="hidden sm:inline-flex">
          <Bell className="h-4 w-4" />
          Triage
          <ArrowUpRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  const OverviewTab = (
    <motion.div {...pageAnim} className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Active Subscribers"
          value={formatCompact(overviewKpis.activeSubs)}
          hint={`Trialing: ${formatCompact(overviewKpis.trialSubs)} • Canceled: ${formatCompact(overviewKpis.canceledSubs)}`}
          icon={Users}
          tone="blue"
        />
        <Stat
          label="MRR"
          value={formatMoney(overviewKpis.mrr)}
          hint={`ARR: ${formatMoney(overviewKpis.arr)}`}
          icon={CreditCard}
          tone="green"
        />
        <Stat
          label="Revenue This Month"
          value={formatMoney(overviewKpis.revenueMonth)}
          hint={`New subs: ${formatCompact(subDashboard?.dashboard?.new_subscribers_this_month ?? 0)}`}
          icon={Activity}
          tone="purple"
        />
        <Stat
          label="Support Queue"
          value={formatCompact(overviewKpis.openInquiries)}
          hint={`Issue reports: ${formatCompact(overviewKpis.issueTotal)}`}
          icon={MessageSquare}
          tone={overviewKpis.openInquiries > 0 ? "yellow" : "gray"}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <GlassCard className="lg:col-span-2">
          <CardHeader
            title="Revenue by Plan"
            subtitle="Active subscriptions only (from /api/admin/subscriptions/statistics)"
            right={<Badge tone="green">Live</Badge>}
          />
          <CardBody className="pt-4">
            {revenueByPlan.length ? (
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueByPlan} margin={{ left: 6, right: 6 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="plan_type" stroke="rgba(255,255,255,0.5)" fontSize={12} />
                    <YAxis stroke="rgba(255,255,255,0.5)" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        background: "rgba(10,10,18,0.9)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        borderRadius: 14,
                        color: "white",
                      }}
                      formatter={(v, k) =>
                        k === "total_revenue" ? [formatMoney(v), "Revenue"] : [v, "Subscribers"]
                      }
                    />
                    <Bar dataKey="total_revenue" radius={[10, 10, 0, 0]} fill="rgba(255,255,255,0.7)" />
                    <Bar dataKey="subscriber_count" radius={[10, 10, 0, 0]} fill="rgba(255,255,255,0.25)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState
                icon={BarChart3}
                title="No revenue data yet"
                subtitle="Once you have active subscriptions, revenue will appear here automatically."
              />
            )}
          </CardBody>
        </GlassCard>

        <GlassCard>
          <CardHeader
            title="Status Distribution"
            subtitle="Subscriptions by status"
            right={<Badge tone="blue">Auto</Badge>}
          />
          <CardBody className="pt-4">
            {statusDistribution.length ? (
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusDistribution}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={2}
                    >
                      {statusDistribution.map((_, i) => (
                        <Cell
                          key={i}
                          fill={
                            [
                              "rgba(255,255,255,0.75)",
                              "rgba(255,255,255,0.45)",
                              "rgba(255,255,255,0.25)",
                              "rgba(255,255,255,0.15)",
                            ][i % 4]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "rgba(10,10,18,0.9)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        borderRadius: 14,
                        color: "white",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState
                icon={AlertCircle}
                title="No status distribution"
                subtitle="As subscriptions are created, you’ll see the breakdown here."
              />
            )}

            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="text-xs text-white/55">Trial → Paid</div>
                <div className="mt-1 text-sm font-semibold text-white">
                  {formatPercent(overviewKpis.conv)}
                </div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="text-xs text-white/55">Churn (30d)</div>
                <div className="mt-1 text-sm font-semibold text-white">
                  {formatPercent(overviewKpis.churn)}
                </div>
              </div>
            </div>
          </CardBody>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <GlassCard>
          <CardHeader
            title="Top Plans"
            subtitle="Most active subscribers"
            right={<Badge tone="gray">This month</Badge>}
          />
          <CardBody className="pt-4">
            {(subDashboard?.dashboard?.top_plans || []).length ? (
              <div className="space-y-2">
                {(subDashboard.dashboard.top_plans || []).map((p, idx) => (
                  <div
                    key={`${p.plan_name}-${idx}`}
                    className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-sm font-semibold text-white/80">
                        {idx + 1}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white/90">{p.plan_name}</div>
                        <div className="text-xs text-white/50">Active subscribers</div>
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-white">{formatCompact(p.subscriber_count)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon={Layers} title="No active plans yet" subtitle="Create a plan and start selling to see this list." />
            )}
          </CardBody>
        </GlassCard>

        <GlassCard>
          <CardHeader
            title="Latest Payments"
            subtitle="Recent completed payments (admin endpoint)"
            right={
              <Button variant="ghost" size="sm" onClick={() => onNavigate("payments")}>
                View all <ArrowUpRight className="h-4 w-4" />
              </Button>
            }
          />
          <CardBody className="pt-4">
            {recentPayments.length ? (
              <div className="space-y-2">
                {recentPayments.slice(0, 6).map((p) => (
                  <div
                    key={p.id || `${p.invoice_number || "inv"}-${p.created_at}`}
                    className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 p-3"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-white/90">
                        {p.invoice_number || p.paypal_subscription_id || "Payment"}
                      </div>
                      <div className="mt-0.5 text-xs text-white/50">
                        {p.status ? `Status: ${p.status}` : ""} {p.paid_at ? `• ${new Date(p.paid_at).toLocaleString()}` : ""}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-white">{formatMoney(p.amount || 0)}</div>
                      <div className="text-xs text-white/50">{p.currency || ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon={CreditCard} title="No payments loaded" subtitle="When payments are recorded, they’ll show up here." />
            )}
          </CardBody>
        </GlassCard>
      </div>
    </motion.div>
  );

  const SubscriptionsTab = (
    <motion.div {...pageAnim} className="space-y-6">
      <GlassCard>
        <CardHeader
          title="Subscriptions"
          subtitle="/api/admin/subscriptions/all — filter by status & plan_type"
          right={
            <div className="flex items-center gap-2">
              <Button variant="subtle" size="sm" onClick={() => loadSubscriptions({ page: 1 })} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Reload
              </Button>
              <Button variant="ghost" size="sm" onClick={() => {
                const blob = new Blob([JSON.stringify(subsRows, null, 2)], { type: "application/json" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = "subscriptions.json";
                a.click();
              }}>
                <Download className="h-4 w-4" /> Export
              </Button>
            </div>
          }
        />
        <CardBody className="pt-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
              <Input
                value={subsFilters.q}
                onChange={(e) => setSubsFilters((s) => ({ ...s, q: e.target.value }))}
                placeholder="Search in loaded results…"
                className="pl-9"
              />
            </div>
            <Select
              value={subsFilters.status}
              onChange={(e) => setSubsFilters((s) => ({ ...s, status: e.target.value }))}
            >
              <option value="">All statuses</option>
              <option value="active">active</option>
              <option value="trialing">trialing</option>
              <option value="canceled">canceled</option>
              <option value="past_due">past_due</option>
            </Select>
            <Select
              value={subsFilters.plan_type}
              onChange={(e) => setSubsFilters((s) => ({ ...s, plan_type: e.target.value }))}
            >
              <option value="">All plans</option>
              {plans.map((p) => (
                <option key={p.id || p.plan_type} value={p.plan_type || ""}>
                  {p.plan_type || p.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="text-xs text-white/55">
              Total (server): <span className="font-semibold text-white/80">{formatCompact(subsPagination.total || 0)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="subtle"
                size="sm"
                disabled={busy || (subsPagination.page || 1) <= 1}
                onClick={() => loadSubscriptions({ page: (subsPagination.page || 1) - 1 })}
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <div className="text-xs text-white/55">
                Page <span className="font-semibold text-white/80">{subsPagination.page || 1}</span> / {subsPagination.pages || 1}
              </div>
              <Button
                variant="subtle"
                size="sm"
                disabled={busy || (subsPagination.page || 1) >= (subsPagination.pages || 1)}
                onClick={() => loadSubscriptions({ page: (subsPagination.page || 1) + 1 })}
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl border border-white/10">
            <div className="max-h-[520px] overflow-auto">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 z-10 bg-[#0B0B12]/95 backdrop-blur border-b border-white/10">
                  <tr className="text-xs text-white/55">
                    <th className="px-4 py-3">Subscription</th>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Cycle</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Price</th>
                    <th className="px-4 py-3">Period End</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/8">
                  {(subsRows || []).map((s) => {
                    const status = String(s.status || "").toLowerCase();
                    const tone =
                      status === "active" ? "green" : status === "trialing" ? "yellow" : status === "canceled" ? "red" : "gray";

                    return (
                      <tr key={s.id || s.paypal_subscription_id || JSON.stringify(s)} className="hover:bg-white/[0.03]">
                        <td className="px-4 py-3">
                          <div className="text-sm font-semibold text-white/90">#{s.id ?? "—"}</div>
                          <div className="mt-0.5 text-xs text-white/45 truncate max-w-[260px]">
                            {s.paypal_subscription_id || s.stripe_subscription_id || s.external_id || "—"}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-semibold text-white/90">{s.user.first_name ?? "—"} {s.user.last_name ?? "—"}</div>
                          <div className="mt-0.5 text-xs text-white/45">{s.user_email || s.user.email || ""}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-white/85">{s.plan_type || s.plan.name || s.plan_id || "—"}</div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone="gray">{s.billing_cycle || "—"}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone={tone}>{status || "—"}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-semibold text-white/90">{formatMoney(s.price_snapshot || s.price || 0)}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-white/80">
                            {s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : "—"}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {!subsRows?.length ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10">
                        <EmptyState
                          icon={CreditCard}
                          title="No subscriptions loaded"
                          subtitle="Click Reload to fetch subscriptions. If you see 403, your account needs admin access."
                          action={
                            <Button variant="subtle" onClick={() => loadSubscriptions({ page: 1 })}>
                              <RefreshCw className="h-4 w-4" /> Reload
                            </Button>
                          }
                        />
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-end">
            <Button
              variant="subtle"
              onClick={() => {
                // reapply client-side query without refetch
                setSubsRows((rows) => {
                  const items = rows || [];
                  if (!subsFilters.q) return items;
                  return items.filter((x) => JSON.stringify(x).toLowerCase().includes(subsFilters.q.toLowerCase()));
                });
              }}
            >
              Apply Search
            </Button>
          </div>
        </CardBody>
      </GlassCard>
    </motion.div>
  );

  const PlansTab = (
    <motion.div {...pageAnim} className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-lg font-semibold text-white/90">Pricing Plans</div>
          <div className="mt-1 text-sm text-white/55">
            Listing comes from <code className="rounded bg-white/5 px-1.5 py-0.5 text-white/75">GET /api/subscriptions/plans</code>.
            Create/update/delete uses admin endpoints.
          </div>
        </div>

        <Button onClick={() => setPlanModalOpen(true)}>
          <CreditCard className="h-4 w-4" /> New Plan
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {plans.map((p) => {
          const active = Boolean(p.is_active ?? true);
          return (
            <GlassCard key={p.id || p.plan_type}>
              <CardHeader
                title={p.name || p.plan_type}
                subtitle={p.plan_type ? `Type: ${p.plan_type}` : ""}
                right={<Badge tone={active ? "green" : "gray"}>{active ? "Active" : "Inactive"}</Badge>}
              />
              <CardBody className="pt-4">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-baseline justify-between">
                    <div className="text-2xl font-semibold text-white">{formatMoney(p.monthly_price || 0)}</div>
                    <div className="text-xs text-white/55">/ month</div>
                  </div>
                  <div className="mt-1 flex items-baseline justify-between">
                    <div className="text-sm font-semibold text-white/80">{formatMoney(p.annual_price || 0)}</div>
                    <div className="text-xs text-white/55">/ year</div>
                  </div>

                  <div className="mt-3 text-xs text-white/55">
                    Interviews/month: <span className="font-semibold text-white/80">{p.interviews_per_month ?? "—"}</span>
                  </div>
                  {p.description ? <div className="mt-2 text-xs text-white/50">{p.description}</div> : null}
                </div>

                <div className="mt-4 flex gap-2">
                  <Button
                    variant="subtle"
                    className="flex-1"
                    onClick={() => updatePlan(p.id, { is_active: !active })}
                    disabled={busy || !p.id}
                  >
                    {active ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                    {active ? "Disable" : "Enable"}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => deletePlan(p.id)}
                    disabled={busy || !p.id}
                    title={!p.id ? "Missing plan id" : "Delete plan"}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardBody>
            </GlassCard>
          );
        })}

        {!plans.length ? (
          <div className="lg:col-span-3">
            <EmptyState
              icon={Layers}
              title="No plans found"
              subtitle="If your DB has plans, make sure they are is_active=True or check GET /api/subscriptions/plans."
              action={<Button onClick={bootstrap}>Reload</Button>}
            />
          </div>
        ) : null}
      </div>

      <Modal
        open={planModalOpen}
        title="Create Pricing Plan"
        onClose={() => setPlanModalOpen(false)}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setPlanModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={createPlan}
              disabled={
                busy ||
                !String(planForm.plan_type || "").trim() ||
                !String(planForm.name || "").trim() ||
                String(planForm.monthly_price || "").trim() === "" ||
                String(planForm.annual_price || "").trim() === "" ||
                String(planForm.interviews_per_month || "").trim() === ""
              }
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Create
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <div className="mb-1 text-xs font-semibold text-white/60">Plan Type</div>
            <Input
              value={planForm.plan_type}
              onChange={(e) => setPlanForm((s) => ({ ...s, plan_type: e.target.value }))}
              placeholder="starter / professional / executive"
            />
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold text-white/60">Name</div>
            <Input
              value={planForm.name}
              onChange={(e) => setPlanForm((s) => ({ ...s, name: e.target.value }))}
              placeholder="Starter"
            />
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold text-white/60">Monthly Price</div>
            <Input
              type="number"
              value={planForm.monthly_price}
              onChange={(e) => setPlanForm((s) => ({ ...s, monthly_price: e.target.value }))}
              placeholder="e.g. 9.99"
            />
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold text-white/60">Annual Price</div>
            <Input
              type="number"
              value={planForm.annual_price}
              onChange={(e) => setPlanForm((s) => ({ ...s, annual_price: e.target.value }))}
              placeholder="e.g. 99.00"
            />
          </div>
          <div className="sm:col-span-2">
            <div className="mb-1 text-xs font-semibold text-white/60">Interviews / month</div>
            <Input
              type="number"
              value={planForm.interviews_per_month}
              onChange={(e) => setPlanForm((s) => ({ ...s, interviews_per_month: e.target.value }))}
              placeholder="e.g. 10"
            />
          </div>
          <div className="sm:col-span-2">
            <div className="mb-1 text-xs font-semibold text-white/60">Description</div>
            <textarea
              value={planForm.description}
              onChange={(e) => setPlanForm((s) => ({ ...s, description: e.target.value }))}
              className="min-h-[90px] w-full resize-none rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/15"
              placeholder="Short description shown on pricing page…"
            />
          </div>
          <div className="sm:col-span-2 flex items-center justify-between rounded-xl border border-white/10 bg-white/5 p-3">
            <div>
              <div className="text-sm font-semibold text-white/80">Active</div>
              <div className="text-xs text-white/50">Visible in pricing page (GET /api/subscriptions/plans).</div>
            </div>
            <button
              onClick={() => setPlanForm((s) => ({ ...s, is_active: !s.is_active }))}
              className={clsx(
                "relative h-7 w-12 rounded-full border transition",
                planForm.is_active ? "border-emerald-500/25 bg-emerald-500/20" : "border-white/10 bg-white/5"
              )}
            >
              <span
                className={clsx(
                  "absolute top-0.5 h-6 w-6 rounded-full bg-white/90 transition",
                  planForm.is_active ? "left-[22px]" : "left-0.5"
                )}
              />
            </button>
          </div>
        </div>

        <div className="mt-3 rounded-xl border border-amber-500/20 bg-amber-500/10 p-3 text-xs text-amber-100">
          Required by backend: <span className="font-semibold">plan_type, name, monthly_price, annual_price, interviews_per_month</span>
        </div>
      </Modal>
    </motion.div>
  );

  const PaymentsTab = (
    <motion.div {...pageAnim} className="space-y-6">
      <GlassCard>
        <CardHeader
          title="Recent Payments"
          subtitle="/api/admin/subscriptions/payments/recent"
          right={
            <Button variant="subtle" size="sm" onClick={() => loadPayments(50)} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Reload
            </Button>
          }
        />
        <CardBody className="pt-4">
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <div className="max-h-[580px] overflow-auto">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 z-10 bg-[#0B0B12]/95 backdrop-blur border-b border-white/10">
                  <tr className="text-xs text-white/55">
                    <th className="px-4 py-3">Invoice</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Paid at</th>
                    <th className="px-4 py-3">Provider</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/8">
                  {recentPayments.map((p) => {
                    const st = String(p.status || "").toLowerCase();
                    const tone = st === "completed" ? "green" : st === "failed" ? "red" : "gray";
                    return (
                      <tr key={p.id || `${p.invoice_number}-${p.created_at}`} className="hover:bg-white/[0.03]">
                        <td className="px-4 py-3">
                          <div className="text-sm font-semibold text-white/90">{p.invoice_number || "—"}</div>
                          <div className="mt-0.5 text-xs text-white/45">{p.user_id ? `User: ${p.user_id}` : ""}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-semibold text-white/90">{formatMoney(p.amount || 0)}</div>
                          <div className="mt-0.5 text-xs text-white/45">{p.currency || ""}</div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone={tone}>{st || "—"}</Badge>
                        </td>
                        <td className="px-4 py-3 text-white/80">
                          {p.paid_at ? new Date(p.paid_at).toLocaleString() : "—"}
                        </td>
                        <td className="px-4 py-3 text-white/70">{p.provider || p.gateway || "—"}</td>
                      </tr>
                    );
                  })}

                  {!recentPayments.length ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-10">
                        <EmptyState
                          icon={BarChart3}
                          title="No payments"
                          subtitle="If payments exist, verify the admin endpoint is reachable and your token is admin."
                          action={
                            <Button variant="subtle" onClick={() => loadPayments(25)}>
                              <RefreshCw className="h-4 w-4" /> Reload
                            </Button>
                          }
                        />
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </CardBody>
      </GlassCard>
    </motion.div>
  );

  const SupportTab = (
    <motion.div {...pageAnim} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <GlassCard>
          <CardHeader
            title="Issue Reports"
            subtitle="/api/issue-reports (admin list)"
            right={<Badge tone="yellow">{formatCompact(issues.total || 0)} total</Badge>}
          />
          <CardBody className="pt-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="relative sm:col-span-2">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                <Input value={supportQ} onChange={(e) => setSupportQ(e.target.value)} placeholder="Search reports (q)…" className="pl-9" />
              </div>
              <Select value={issueStatus} onChange={(e) => setIssueStatus(e.target.value)}>
                <option value="">All statuses</option>
                <option value="new">new</option>
                <option value="triaged">triaged</option>
                <option value="in_progress">in_progress</option>
                <option value="needs_info">needs_info</option>
                <option value="resolved">resolved</option>
                <option value="closed">closed</option>
              </Select>
            </div>

            <div className="mt-3 flex items-center justify-between">
              <div className="text-xs text-white/55">
                Offset: <span className="font-semibold text-white/80">{issues.offset || 0}</span> • Limit: {issues.limit || 50}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="subtle"
                  size="sm"
                  disabled={busy || (issues.offset || 0) <= 0}
                  onClick={() => loadSupport({ issuesOffset: Math.max(0, (issues.offset || 0) - (issues.limit || 50)), inquiriesPage: inquiries.page || 1 })}
                >
                  <ChevronLeft className="h-4 w-4" /> Prev
                </Button>
                <Button
                  variant="subtle"
                  size="sm"
                  disabled={busy || (issues.offset || 0) + (issues.limit || 50) >= (issues.total || 0)}
                  onClick={() => loadSupport({ issuesOffset: (issues.offset || 0) + (issues.limit || 50), inquiriesPage: inquiries.page || 1 })}
                >
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => loadSupport({ issuesOffset: 0, inquiriesPage: 1 })}
                  disabled={busy}
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Apply
                </Button>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {(issues.items || []).slice(0, 12).map((r) => {
                const st = String(r.status || "new").toLowerCase();
                const sev = String(r.severity || "").toLowerCase();
                const tone = st === "resolved" || st === "closed" ? "green" : st === "in_progress" ? "yellow" : "gray";
                const sevTone = sev === "critical" ? "red" : sev === "high" ? "yellow" : "gray";
                return (
                  <div key={r.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="truncate text-sm font-semibold text-white/90">{r.title || `Report #${r.id}`}</div>
                          <Badge tone={sevTone}>{sev || "—"}</Badge>
                          <Badge tone={tone}>{st}</Badge>
                        </div>
                        <div className="mt-1 max-h-10 overflow-hidden text-xs text-white/55">{r.description || ""}</div>
                        <div className="mt-2 text-xs text-white/45">
                          {r.report_type ? `Type: ${r.report_type}` : ""} {r.created_at ? `• ${new Date(r.created_at).toLocaleString()}` : ""}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Select
                          value={st}
                          onChange={(e) => updateIssueReportStatus(r.id, e.target.value)}
                          className="h-9 text-xs"
                        >
                          <option value="new">new</option>
                          <option value="triaged">triaged</option>
                          <option value="in_progress">in_progress</option>
                          <option value="needs_info">needs_info</option>
                          <option value="resolved">resolved</option>
                          <option value="closed">closed</option>
                        </Select>
                      </div>
                    </div>
                  </div>
                );
              })}

              {!issues.items?.length ? (
                <EmptyState
                  icon={AlertCircle}
                  title="No reports"
                  subtitle="Try widening filters or ensure your admin token is valid."
                  action={
                    <Button variant="subtle" onClick={() => loadSupport({ issuesOffset: 0, inquiriesPage: 1 })}>
                      <RefreshCw className="h-4 w-4" /> Reload
                    </Button>
                  }
                />
              ) : null}
            </div>
          </CardBody>
        </GlassCard>

        <GlassCard>
          <CardHeader
            title="Contact Inquiries"
            subtitle="/api/contact/inquiries (admin)"
            right={<Badge tone="blue">{formatCompact(inquiries.total || 0)} total</Badge>}
          />
          <CardBody className="pt-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Select value={inquiryStatus} onChange={(e) => setInquiryStatus(e.target.value)} className="sm:col-span-1">
                <option value="">All statuses</option>
                <option value="new">new</option>
                <option value="in_progress">in_progress</option>
                <option value="replied">replied</option>
                <option value="resolved">resolved</option>
                <option value="closed">closed</option>
              </Select>
              <div className="sm:col-span-2 flex items-center justify-end gap-2">
                <Button
                  variant="subtle"
                  size="sm"
                  disabled={busy || (inquiries.page || 1) <= 1}
                  onClick={() => loadSupport({ issuesOffset: issues.offset || 0, inquiriesPage: (inquiries.page || 1) - 1 })}
                >
                  <ChevronLeft className="h-4 w-4" /> Prev
                </Button>
                <div className="text-xs text-white/55">
                  Page <span className="font-semibold text-white/80">{inquiries.page || 1}</span> / {inquiries.total_pages || 1}
                </div>
                <Button
                  variant="subtle"
                  size="sm"
                  disabled={busy || (inquiries.page || 1) >= (inquiries.total_pages || 1)}
                  onClick={() => loadSupport({ issuesOffset: issues.offset || 0, inquiriesPage: (inquiries.page || 1) + 1 })}
                >
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => loadSupport({ issuesOffset: issues.offset || 0, inquiriesPage: 1 })}
                  disabled={busy}
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Apply
                </Button>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {(inquiries.items || []).slice(0, 12).map((i) => {
                const st = String(i.status || "new").toLowerCase();
                const tone = st === "resolved" || st === "closed" ? "green" : st === "in_progress" ? "yellow" : "gray";
                return (
                  <div key={i.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="truncate text-sm font-semibold text-white/90">{i.full_name || "Unknown"}</div>
                          <Badge tone={tone}>{st}</Badge>
                        </div>
                        <div className="mt-0.5 text-xs text-white/55">{i.email || ""}</div>
                        <div className="mt-2 max-h-10 overflow-hidden text-xs text-white/50">{i.message || ""}</div>
                        <div className="mt-2 text-xs text-white/45">
                          {i.inquiry_type ? `Type: ${i.inquiry_type}` : ""} {i.created_at ? `• ${new Date(i.created_at).toLocaleString()}` : ""}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {st !== "resolved" ? (
                          <Button
                            variant="subtle"
                            size="sm"
                            onClick={() => resolveInquiry(i.id)}
                            disabled={busy}
                          >
                            <Check className="h-4 w-4" /> Resolve
                          </Button>
                        ) : (
                          <Badge tone="green">
                            <Check className="h-3.5 w-3.5" /> Done
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {!inquiries.items?.length ? (
                <EmptyState
                  icon={Mail}
                  title="No inquiries"
                  subtitle="Try changing the status filter or confirm /api/contact/inquiries is enabled for admins."
                />
              ) : null}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="text-xs text-white/55">Response rate</div>
                <div className="mt-1 text-sm font-semibold text-white">
                  {formatPercent(contactStats?.statistics?.response_rate ?? 0)}
                </div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="text-xs text-white/55">New</div>
                <div className="mt-1 text-sm font-semibold text-white">
                  {formatCompact(contactStats?.statistics?.by_status?.new ?? 0)}
                </div>
              </div>
            </div>
          </CardBody>
        </GlassCard>
      </div>
    </motion.div>
  );

  const SettingsTab = (
    <motion.div {...pageAnim} className="space-y-6">
      <GlassCard>
        <CardHeader title="Admin Settings" subtitle="Design-ready. Wire it to your config endpoints when you add them." right={<Badge tone="gray">Coming soon</Badge>} />
        <CardBody>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center gap-2">
                <Flag className="h-4 w-4 text-white/70" />
                <div className="text-sm font-semibold text-white/85">Feature Flags</div>
              </div>
              <div className="mt-1 text-xs text-white/55">Add endpoints and we’ll plug them into this UI instantly.</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-white/70" />
                <div className="text-sm font-semibold text-white/85">Operational Limits</div>
              </div>
              <div className="mt-1 text-xs text-white/55">Rate limits, trial days, alerts, and provider keys.</div>
            </div>
          </div>
        </CardBody>
      </GlassCard>
    </motion.div>
  );

  const ActiveTab = () => {
    if (bootLoading) {
      return (
        <motion.div {...pageAnim} className="space-y-6">
          <GlassCard>
            <CardBody>
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-white/80" />
                <div>
                  <div className="text-sm font-semibold text-white/85">Loading admin data…</div>
                  <div className="mt-0.5 text-xs text-white/55">Fetching dashboard, stats, support, and plans.</div>
                </div>
              </div>
            </CardBody>
          </GlassCard>
        </motion.div>
      );
    }

    if (bootError) {
      const status = bootError?.status;
      const isAuth = status === 401 || status === 403;

      return (
        <motion.div {...pageAnim} className="space-y-6">
          <GlassCard>
            <CardBody>
              <div className="flex items-start gap-3">
                <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 p-2">
                  <AlertCircle className="h-5 w-5 text-rose-200" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white/90">Couldn’t load admin dashboard</div>
                  <div className="mt-1 text-xs text-white/55">
                    {isAuth
                      ? "Your token is missing or you don’t have admin access (401/403)."
                      : "An API request failed. Check the backend logs + network tab."}
                  </div>
                  <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-white/60">
                    <div className="font-semibold text-white/75">Error</div>
                    <div className="mt-1 break-words">{String(bootError.message || bootError)}</div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button onClick={bootstrap}>
                      <RefreshCw className="h-4 w-4" /> Retry
                    </Button>
                    <Button variant="subtle" onClick={() => alert("Make sure your frontend sends Authorization: Bearer <token> and your user.is_admin = True")}
                    >
                      <ShieldAlert className="h-4 w-4" /> Checklist
                    </Button>
                  </div>
                </div>
              </div>
            </CardBody>
          </GlassCard>
        </motion.div>
      );
    }

    if (active === "overview") return OverviewTab;
    if (active === "subscriptions") return SubscriptionsTab;
    if (active === "plans") return PlansTab;
    if (active === "payments") return PaymentsTab;
    if (active === "support") return SupportTab;
    if (active === "settings") return SettingsTab;
    return OverviewTab;
  };

  return (
    <div className="min-h-screen bg-[#07090c] text-white">
      {/* premium background */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-emerald-400/10 blur-[100px]" />
        <div className="absolute -bottom-44 -right-44 h-[560px] w-[560px] rounded-full bg-indigo-400/10 blur-[110px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:22px_22px] opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/40" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-[1400px]">
        {/* Sidebar */}
        <aside className="hidden w-72 shrink-0 flex-col border-r border-white/8 bg-black/10 backdrop-blur-xl lg:flex">
          <div className="p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-black shadow-[0_14px_40px_-20px_rgba(255,255,255,0.5)]">
                <LayoutDashboard className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white/90">Admin</div>
                <div className="text-xs text-white/55">Control Center</div>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-3">
              <div className="text-xs text-white/55">MRR</div>
              <div className="mt-1 text-lg font-semibold text-white">{formatMoney(overviewKpis.mrr)}</div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-1.5 rounded-full bg-white/70"
                  style={{ width: `${Math.min(100, (Number(overviewKpis.mrr || 0) / 5000) * 100)}%` }}
                />
              </div>
              <div className="mt-1 text-[11px] text-white/45">Visual gauge (edit denominator anytime)</div>
            </div>
          </div>

          <div className="px-3 pb-6">
            <div className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-white/35">Navigation</div>
            <div className="space-y-1">
              {NAV.map((n) => {
                const activeTab = active === n.id;
                const Icon = n.icon;
                const badge = navBadges[n.id];
                return (
                  <button
                    key={n.id}
                    onClick={() => onNavigate(n.id)}
                    className={clsx(
                      "group flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left transition",
                      activeTab ? "bg-white text-black" : "text-white/70 hover:bg-white/5 hover:text-white"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={clsx("h-4 w-4", activeTab ? "text-black" : "text-white/70 group-hover:text-white")} />
                      <span className="text-sm font-semibold">{n.label}</span>
                    </div>
                    {badge ? (
                      <span className="rounded-full bg-black/10 px-2 py-0.5 text-xs font-semibold text-black">
                        {badge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-auto p-6">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-white/55">Quick actions</div>
                <Badge tone="gray">Beta</Badge>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button variant="subtle" size="sm" onClick={() => onNavigate("plans")}>
                  <CreditCard className="h-4 w-4" /> Plans
                </Button>
                <Button variant="subtle" size="sm" onClick={() => onNavigate("support")}>
                  <Bell className="h-4 w-4" /> Triage
                </Button>
              </div>
            </div>
          </div>
        </aside>

        {/* Main */}
        <div className="min-w-0 flex-1">
          {/* Top bar */}
          <div className="sticky top-0 z-40 border-b border-white/8 bg-black/10 backdrop-blur-xl">
            <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between gap-4">
                <div className="lg:hidden">
                  <div className="text-sm font-semibold text-white/90">Admin Control Center</div>
                  <div className="text-xs text-white/55">{NAV.find((x) => x.id === active)?.label || "Overview"}</div>
                </div>

                <div className="hidden lg:block">{Header}</div>

                <div className="flex items-center gap-2 lg:hidden">
                  <Button variant="subtle" size="sm" onClick={refreshAll} disabled={busy || bootLoading}>
                    {busy || bootLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Mobile nav */}
              <div className="mt-4 flex gap-2 overflow-x-auto pb-1 lg:hidden">
                {NAV.map((n) => {
                  const Icon = n.icon;
                  const activeTab = active === n.id;
                  return (
                    <button
                      key={n.id}
                      onClick={() => onNavigate(n.id)}
                      className={clsx(
                        "flex items-center gap-2 whitespace-nowrap rounded-xl border px-3 py-2 text-xs font-semibold",
                        activeTab
                          ? "border-white/20 bg-white text-black"
                          : "border-white/10 bg-white/5 text-white/70"
                      )}
                    >
                      <Icon className="h-4 w-4" /> {n.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <AnimatePresence mode="wait">
              <ActiveTab key={active} />
            </AnimatePresence>
          </main>
        </div>
      </div>
    </div>
  );
}
